import { observer } from 'mobx-react-lite'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import InputMask from 'react-input-mask'
import { useNavigate } from 'react-router-dom'
import { SelectInstance } from 'react-select'

import { useStore } from 'store/useStore'
import { countries } from 'sections/LKSection/modules/UserModule/data'
import { LoginStatus } from 'sections/LKSection/modules/UserModule/stores/LoginUiStore'
import { promoColors } from 'sections/PromoSection/shared/constants'
import { CountryItem } from 'components/CountryPhoneInput/CountryPhoneInput'
import { masks, phonePrefix } from 'components/CountryPhoneInput/data'
import { ButtonPromo, InputPromo, TextPromo } from 'components/ui'
import { Space } from 'components/ui'
import { Flex } from 'components/uiKit'
import { formatPhoneNumberForText } from 'utils/formatting/formatPhoneNumber/formatPhomeNumberForText/formatPhoneNumberForText'
import { isGeDomain } from 'utils/helpers/domains/getDomain'
import { sendEvent } from 'utils/helpers/sendEvent'
import { useResize } from 'utils/hooks/useResize'
import { ALL_EXCEPT_NUMBERS_REGEXP } from 'shared/constants/regExp'
import { AuthRoutesEnum, LKRoutesEnum } from 'shared/constants/routes'
import { URLS } from 'shared/constants/urls'
import { SCROLL_TO_ID } from 'shared/constants/values'
import { BaseOption } from 'shared/types/types'
import { EventType } from 'types/types'

import { PromoPageKeys } from '../../types'
import { CountrySelectWithFlags } from './components/customSelect/CountrySelectWithFlags'
import { countrySelectCustomStyles } from './countrySelectCustomStyles'

import s from './OpenPickPointForm.module.scss'

const CODE_LENGTH = 6

export const OpenPickPointForm: FC = observer(() => {
  const { isSTablet, isTablet, isMobile, isSDesktop, isDesktop } = useResize()
  const { t } = useTranslation()

  const { loginUiStore: uiStore, appStore } = useStore()
  const [sixDigitsCode, setSixDigitsCode] = useState(['', '', '', '', '', ''])
  const inputRefs = useRef<HTMLInputElement[]>([])
  const phoneInputRef = useRef<InputMask>(null)
  const selectRef = useRef<SelectInstance<any, boolean> | null>(null)

  const [menuPlacement, setMenuPlacement] = useState<'top' | 'bottom'>('bottom')

  const { timeIsNotOver, timeExpired } = uiStore.timeWaiter

  const navigate = useNavigate()

  const localizedCountries = useMemo(() => {
    countries.sort((a, b) => a.label.localeCompare(b.label))
    return countries.map((country) => ({
      ...country,
      label: t(getSharedKey(country.value.label)),
    }))
  }, [t])

  const [selectedCountry, setSelectedCountry] = useState<BaseOption<CountryItem> | null>(
    isGeDomain()
      ? localizedCountries.find((country) => country.value.country === 'GE') || localizedCountries[5]
      : localizedCountries[5],
  )

  const countryKey = selectedCountry?.value?.country as keyof typeof masks

  useEffect(() => {
    if (!localizedCountries.length) return

    const computedDefaultOption = isGeDomain()
      ? localizedCountries.find((country) => country.value.country === 'GE') || localizedCountries[5]
      : localizedCountries[5]

    setSelectedCountry(computedDefaultOption)
    uiStore.setSelectedCountry(computedDefaultOption.value)
  }, [localizedCountries, uiStore])

  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight
      const rect = selectRef.current?.controlRef?.getBoundingClientRect()

      if (rect) {
        const spaceBelow = windowHeight - rect.bottom
        const spaceAbove = rect.top

        if (spaceBelow < 300 && spaceAbove > 300) {
          setMenuPlacement('top')
        } else {
          setMenuPlacement('bottom')
        }
      }
    }

    handleScroll()
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [menuPlacement])

  const getKey = (key: string) => `${PromoPageKeys.openPickPointForm}.${key}`
  function getSharedKey(key: string) {
    return `${PromoPageKeys.countries}.${key}`
  }

  const handleInputChange = (index: number, value: string) => {
    const digit = value.replace(ALL_EXCEPT_NUMBERS_REGEXP, '')
    const newCode = [...sixDigitsCode]
    newCode[index] = digit
    setSixDigitsCode(newCode)

    if (value.length === 1) {
      const nextInput = inputRefs.current[index + 1]
      if (nextInput) {
        nextInput.focus()
      }
    }
  }

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace') {
      const newCode = [...sixDigitsCode]

      if (!newCode[index] && index > 0) {
        const previousInput = inputRefs.current[index - 1]
        if (previousInput) {
          previousInput.focus()
        }
      }
    }
  }

  const handleSetCode = async () => {
    uiStore.setCode(sixDigitsCode.join(''))

    if (sixDigitsCode.length !== CODE_LENGTH) {
      return
    }

    const result = await uiStore.validateCode()

    if (result) {
      uiStore.clearTimer()
      uiStore.setCode('')
      uiStore.setDefaultState()
      bootstrap()
      sendEvent(EventType.MainFormPUPSend)
    }
  }

  const handlePressBtn = async () => {
    const result = await uiStore.tryLogin(false)
    if (result === LoginStatus.validateCode) {
      bootstrap()
    } else if (result === LoginStatus.loggedIn) {
      uiStore.setValidateVisible(true)
    }
    sendEvent(EventType.MainFormPUPT, { type: 'Заявка' })
  }

  const bootstrap = async () => {
    await appStore.bootstrap()
    const screen = appStore.needRegistration ? AuthRoutesEnum.registration : LKRoutesEnum.orders
    appStore.setLoadingApp(false)
    navigate(screen, { replace: true })
  }

  const handleCountryChange = (item: BaseOption<CountryItem>) => {
    setSelectedCountry(item)
    uiStore.setSelectedCountry(item.value)

    sendEvent(EventType.MainFormPUPT, {
      type: 'Страна',
      type_detail: item.value.label,
    })
  }

  const handleSetDefaultState = () => {
    uiStore.setDefaultState()
    uiStore.setSelectedCountry(selectedCountry?.value)
  }

  return (
    <div className={s.openPPForm} id={SCROLL_TO_ID.authForm}>
      <TextPromo
        size={isSDesktop ? (isSTablet ? 's20' : 's32') : 's40'}
        fontFamily="ASLHaussVF"
        weight="w550"
        id={SCROLL_TO_ID.authFormMobile}
      >
        {!uiStore.isValidateVisible ? t(getKey('Зарегистрируйтесь чтобы подать заявку')) : t(getKey('Введите код'))}
      </TextPromo>
      {uiStore.isValidateVisible && (
        <TextPromo size="s20" fontFamily="ASLHaussVF" className={s.caption}>
          <>
            {t(getKey('Отправили код в профиль Wildberries'), { count: Number(uiStore.phonePrefix) })}{' '}
            {formatPhoneNumberForText(uiStore.phone)}
            <TextPromo
              pointer
              onClick={handleSetDefaultState}
              fontFamily="ASLHauss"
              size="s20"
              weight="w700"
              className={s.caption}
            >
              {' '}
              {t(getKey('Изменить номер'))}
            </TextPromo>
          </>
        </TextPromo>
      )}
      {!uiStore.isValidateVisible ? (
        <>
          <div className={s.form}>
            <Flex className={s.inputWrapper} align="center">
              <CountrySelectWithFlags
                onChange={(item) => handleCountryChange(item as BaseOption<CountryItem>)}
                menuPosition="absolute"
                styles={countrySelectCustomStyles(
                  { menu: { margin: isSDesktop || isDesktop ? 5 : 0, 'margin-top': isMobile ? -5 : 5 } },
                  menuPlacement,
                )}
                className={s.customSelect}
                value={selectedCountry}
                options={localizedCountries}
                isDisabled={isGeDomain()}
                isSearchable={false}
                components={{}}
                ref={selectRef}
              />
              <Flex
                align="center"
                flex={1}
                style={{ cursor: 'pointer', height: '100%' }}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                onClick={() => phoneInputRef.current?.focus()}
              >
                <Flex>
                  <span className={s.countryCode}>{`+${phonePrefix[countryKey]}`}</span>
                </Flex>
                <InputMask
                  value={uiStore.phone}
                  onChange={({ target: { value } }) => uiStore.setPhone(value)}
                  type="tel"
                  inputMode="numeric"
                  mask={masks[countryKey]}
                  maskPlaceholder={null}
                  className={s.input}
                  ref={phoneInputRef}
                />
              </Flex>
            </Flex>
            <ButtonPromo
              className={s.loginButton}
              fullWidth={isMobile}
              onClick={uiStore.allowLogin ? handlePressBtn : undefined}
              variant={!isMobile ? 'arrow' : 'purple'}
              disabled={!uiStore.allowLogin}
            >
              {isMobile && (
                <TextPromo fontFamily="ASLHaussVF" size={isTablet ? 's16' : 's20'} weight="w550" color="white">
                  {t(getKey('Зарегистрироваться'))}
                </TextPromo>
              )}
            </ButtonPromo>
          </div>
          <Space size={isTablet ? (isSTablet ? 32 : 48) : 56} />
          <TextPromo center={isTablet} fontFamily="ASLHauss" size={isMobile ? 's12' : 's16'} weight="w400">
            {t(getKey('Указывая номер телефона'), { nbsp: '\u00A0' })}
            {'\u00A0'}
            <a
              href={isGeDomain() ? URLS.offerGE : URLS.offer}
              target="_blank"
              style={{ fontWeight: 400, color: promoColors.newBgPrimary, textDecoration: 'none' }}
              rel="noreferrer"
              onClick={() => sendEvent(EventType.MainFormPUPT, { type: 'Оферта' })}
              className={s.link}
            >
              {t(getKey('офертой'))}
            </a>{' '}
            {t(getKey('и'))}{' '}
            <a
              href={isGeDomain() ? URLS.wbMarketPrivatePolicyGe : URLS.wbMarketPrivatePolicy}
              target="_blank"
              style={{ fontWeight: 400, color: promoColors.newBgPrimary, textDecoration: 'none' }}
              rel="noreferrer"
              onClick={() => sendEvent(EventType.MainFormPUPT, { type: 'Политика' })}
              className={s.link}
            >
              {t(getKey('политикой конфиденциальности'))}
            </a>
          </TextPromo>
        </>
      ) : (
        <>
          <Space size={24} />
          <Flex gap={isSTablet ? 6 : 16}>
            {sixDigitsCode.map((digit, index) => (
              <InputPromo
                className={s.customInput}
                key={index}
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                value={digit}
                onChange={(e) => handleInputChange(index, e.target.value)}
                onKeyDown={(event) => handleKeyDown(index, event)}
                onFocus={() => {
                  const parentElement = inputRefs.current[index]?.closest(`.${s.customInput}`)
                  if (parentElement) {
                    parentElement.setAttribute('data-active', 'true')
                  }
                }}
                onBlur={(e) => {
                  const parentElement = e.target.closest(`.${s.customInput}`)
                  if (parentElement) {
                    parentElement.removeAttribute('data-active')
                  }

                  if (e.target.value.length === 1) {
                    const nextInput = inputRefs.current[index + 1]
                    if (nextInput) {
                      nextInput.focus()
                    }
                  }
                }}
                inputRef={(ref) => {
                  inputRefs.current[index] = ref as HTMLInputElement
                }}
              />
            ))}
          </Flex>
          <Space size={46} />
          <ButtonPromo
            onClick={sixDigitsCode.join('').length === 6 ? handleSetCode : undefined}
            disabled={sixDigitsCode.join('').length !== CODE_LENGTH}
            fullWidth
            variant="purple"
          >
            <TextPromo pointer fontFamily="ASLHaussVF" size="s20" color="white">
              {t(getKey('Подтвердить'))}
            </TextPromo>
          </ButtonPromo>
          <Space size={24} />
          <TextPromo
            onClick={!timeIsNotOver ? () => uiStore.tryLogin(true) : undefined}
            style={{ textAlign: 'center' }}
            fontFamily="ASLHauss"
            size="s16"
            pointer
            weight="w700"
          >
            {timeIsNotOver
              ? t(getKey('Отправить заново через'), { count: Number(timeExpired) })
              : t(getKey('Отправить код подтверждения'))}
          </TextPromo>
        </>
      )}
    </div>
  )
})
